import { memo, useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SendOutlined, PaperClipOutlined, LoadingOutlined } from "@ant-design/icons";
import { popupSendMessageRequest } from "providers/ChatPopupProvider/slice";
import Helper from "utils/helpers";

const ChatPopupMessageInput = ({ conversationId }) => {
  const dispatch = useDispatch();
  const textRef = useRef(null);
  const fileInputRef = useRef(null);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const isSending = useSelector(
    (state) => state.chatPopup.sendingWindows[conversationId] || false
  );

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    setText("");
    dispatch(
      popupSendMessageRequest({
        type: "text",
        text: trimmed,
        conversationId,
        attachments: [],
      })
    );

    // Re-focus input
    setTimeout(() => textRef.current?.focus(), 0);
  }, [text, isSending, dispatch, conversationId]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);
      try {
        const isVideo = file.type.startsWith("video/");
        let result;
        if (isVideo) {
          result = await Helper.uploadVideo({ file });
        } else {
          result = await Helper.uploadFile({ file });
        }

        if (result) {
          const attachment = {
            type: result.attachmentType,
            data: result.attachmentType === "image"
              ? { imageUrl: result.url }
              : { videoUrl: result.url },
          };
          dispatch(
            popupSendMessageRequest({
              type: "text",
              text: text.trim() || null,
              conversationId,
              attachments: [attachment],
            })
          );
          setText("");
        }
      } catch (err) {
        console.error("Upload failed:", err);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [dispatch, conversationId, text]
  );

  return (
    <div className="chat-popup-input">
      <button
        className="chat-popup-input-attach"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? <LoadingOutlined spin /> : <PaperClipOutlined />}
      </button>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*,video/*"
        onChange={handleFileSelect}
      />
      <textarea
        ref={textRef}
        className="chat-popup-input-text"
        placeholder="Nhập tin nhắn..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        disabled={isSending}
      />
      <button
        className="chat-popup-input-send"
        onClick={handleSend}
        disabled={!text.trim() || isSending}
      >
        <SendOutlined />
      </button>
    </div>
  );
};

export default memo(ChatPopupMessageInput);
